import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ColorsService } from './colors.service';
import { CategoryStateManager } from '../utils/category-state-manager';
import { TripImpact, ChartData, CategoryElement, LegendCategory } from '../models';
import { translateElementType } from '../utils/element-type-translation';

/**
 * Configuration for chart and legend data generation
 */
export interface ImpactDataConfig {
  tripImpact: TripImpact;
  fullImpactDetails: {
    transport: Array<{ type: string; impact: number; km: number; isMainTransport: boolean; title?: string }>;
    accommodation: Array<{ type: string; impact: number; title?: string; isEcoFriendly?: boolean }>;
    activities: Array<{ type: string; impact: number; title?: string }>;
    food: Array<{ type: string; impact: number }>;
  };
  viewMode: 'group' | 'individual';
  travelers: number;
  excludeMainTransport: boolean;
  groupTransports: boolean;
  showPercentage: boolean;
  categoryStateManager: CategoryStateManager;
}

/**
 * Service responsible for generating chart and legend data for impact visualization
 * Centralizes complex data transformation logic
 */
@Injectable({
  providedIn: 'root'
})
export class ImpactDataService {
  private transloco = inject(TranslocoService);
  private colorsService = inject(ColorsService);

  /**
   * Generate chart data based on configuration
   */
  generateChartData(config: ImpactDataConfig): ChartData[] {
    if (config.groupTransports) {
      return this.generateChartDataWithTransportGrouping(config);
    }
    return this.generateChartDataByCategoryWithSubItems(config);
  }

  /**
   * Generate legend data based on configuration
   */
  generateLegendData(config: ImpactDataConfig): LegendCategory[] {
    if (config.groupTransports) {
      return this.generateLegendDataWithTransportGrouping(config);
    }
    return this.generateLegendDataByCategory(config);
  }

  /**
   * Calculate current total impact considering view mode and exclusions
   */
  calculateTotalImpact(config: ImpactDataConfig): number {
    let total = config.viewMode === 'group'
      ? config.tripImpact.totalImpact
      : config.tripImpact.impactPerTraveller;

    if (config.excludeMainTransport) {
      const mainTransportImpact = config.fullImpactDetails.transport
        .filter(t => t.isMainTransport)
        .reduce((sum, t) => sum + t.impact, 0);

      if (config.viewMode === 'group') {
        total -= mainTransportImpact;
      } else {
        total -= mainTransportImpact / (config.travelers || 1);
      }
    }

    return Math.max(0, total);
  }

  /**
   * Generate detailed chart data with all category sub-items
   */
  private generateChartDataByCategoryWithSubItems(config: ImpactDataConfig): ChartData[] {
    const allElements: CategoryElement[] = [];

    // Add transports (grouped by type + title)
    const transportGroups = new Map<string, { type: string; impact: number; title?: string }>();
    config.fullImpactDetails.transport.forEach((transport) => {
      if (config.excludeMainTransport && transport.isMainTransport) return;
      const key = transport.title ? `${transport.type}-${transport.title}` : transport.type;
      const existing = transportGroups.get(key);
      if (existing) {
        existing.impact += transport.impact;
      } else {
        transportGroups.set(key, { type: transport.type, impact: transport.impact, title: transport.title });
      }
    });
    transportGroups.forEach((t) => {
      const adjustedImpact = config.viewMode === 'individual' ? t.impact / (config.travelers || 1) : t.impact;
      allElements.push({
        category: 'transport',
        impact: Math.round(adjustedImpact),
        type: t.type,
        label: translateElementType(this.transloco, t.type),
        title: t.title
      });
    });

    // Add accommodations (grouped by type + title + eco status)
    const accommodationGroups = new Map<string, { type: string; impact: number; title?: string }>();
    config.fullImpactDetails.accommodation.forEach((accommodation) => {
      const ecoKey = (accommodation as { isEcoFriendly?: boolean }).isEcoFriendly ? 'eco' : 'std';
      const key = accommodation.title ? `${accommodation.type}-${accommodation.title}-${ecoKey}` : `${accommodation.type}-${ecoKey}`;
      const existing = accommodationGroups.get(key);
      if (existing) {
        existing.impact += accommodation.impact;
      } else {
        accommodationGroups.set(key, { type: accommodation.type, impact: accommodation.impact, title: accommodation.title });
      }
    });
    accommodationGroups.forEach((a) => {
      const adjustedImpact = config.viewMode === 'individual' ? a.impact / (config.travelers || 1) : a.impact;
      allElements.push({
        category: 'accommodation',
        impact: Math.round(adjustedImpact),
        type: a.type,
        label: translateElementType(this.transloco, a.type),
        title: a.title
      });
    });

    // Add activities (grouped by type + title)
    const activityGroups = new Map<string, { type: string; impact: number; title?: string }>();
    config.fullImpactDetails.activities.forEach((activity) => {
      const key = activity.title ? `${activity.type}-${activity.title}` : activity.type;
      const existing = activityGroups.get(key);
      if (existing) {
        existing.impact += activity.impact;
      } else {
        activityGroups.set(key, { type: activity.type, impact: activity.impact, title: activity.title });
      }
    });
    activityGroups.forEach((a) => {
      const adjustedImpact = config.viewMode === 'individual' ? a.impact / (config.travelers || 1) : a.impact;
      allElements.push({
        category: 'activity',
        impact: Math.round(adjustedImpact),
        type: a.type,
        label: translateElementType(this.transloco, a.type),
        title: a.title
      });
    });

    // Add food (grouped by type)
    const foodGroups = new Map<string, { type: string; impact: number }>();
    config.fullImpactDetails.food.forEach((food) => {
      const existing = foodGroups.get(food.type);
      if (existing) {
        existing.impact += food.impact;
      } else {
        foodGroups.set(food.type, { type: food.type, impact: food.impact });
      }
    });
    foodGroups.forEach((f) => {
      const adjustedImpact = config.viewMode === 'individual' ? f.impact / (config.travelers || 1) : f.impact;
      allElements.push({
        category: 'food',
        impact: Math.round(adjustedImpact),
        type: f.type,
        label: translateElementType(this.transloco, f.type)
      });
    });

    // Add waste
    const wasteImpact = config.viewMode === 'group'
      ? config.tripImpact.wasteImpact
      : config.tripImpact.wastePerTraveller;

    allElements.push({
      category: 'waste',
      impact: Math.round(wasteImpact),
      type: 'waste',
      label: this.transloco.translate('impact.categories.waste') || 'Déchets'
    });

    // Calculate total impact per category
    const categoryImpacts = new Map<string, number>();
    allElements.forEach(element => {
      const currentTotal = categoryImpacts.get(element.category) || 0;
      categoryImpacts.set(element.category, currentTotal + element.impact);
    });

    // Sort by category impact descending, then by item impact descending
    allElements.sort((a, b) => {
      const categoryImpactA = categoryImpacts.get(a.category) || 0;
      const categoryImpactB = categoryImpacts.get(b.category) || 0;
      const categoryDiff = categoryImpactB - categoryImpactA;
      if (categoryDiff !== 0) return categoryDiff;
      return b.impact - a.impact;
    });

    // Generate colors with category grouping
    const colors = this.colorsService.generateCategoryGroupedColors(allElements);

    // Convert to ChartData
    const data: ChartData[] = allElements.map((element, index) => ({
      label: element.label,
      value: element.impact,
      color: colors[index] || this.colorsService.getCategoryColor(element.category),
      title: element.title
    }));

    // Convert to percentages if requested
    if (config.showPercentage) {
      const total = data.reduce((sum, item) => sum + item.value, 0);
      return data.map(item => ({
        ...item,
        value: total > 0 ? Math.round((item.value / total) * 100) : 0
      }));
    }

    return data;
  }

  /**
   * Generate chart data with transport grouping (main vs on-site)
   */
  private generateChartDataWithTransportGrouping(config: ImpactDataConfig): ChartData[] {
    const data: ChartData[] = [];

    // Calculate main and on-site transport impacts
    let mainTransportImpact = 0;
    let onSiteTransportImpact = 0;

    config.fullImpactDetails.transport.forEach(transport => {
      if (transport.isMainTransport) {
        mainTransportImpact += transport.impact;
      } else {
        onSiteTransportImpact += transport.impact;
      }
    });

    // Apply view mode
    if (config.viewMode === 'individual') {
      mainTransportImpact = mainTransportImpact / (config.travelers || 1);
      onSiteTransportImpact = onSiteTransportImpact / (config.travelers || 1);
    }

    // Apply main transport exclusion
    if (config.excludeMainTransport) {
      mainTransportImpact = 0;
    }

    // Add grouped transports
    if (mainTransportImpact > 0) {
      data.push({
        label: this.transloco.translate('impact.transportTypes.mainTransport'),
        value: Math.round(mainTransportImpact),
        color: this.colorsService.MAIN_TRANSPORT_COLOR
      });
    }

    if (onSiteTransportImpact > 0) {
      data.push({
        label: this.transloco.translate('impact.transportTypes.onSiteTransport'),
        value: Math.round(onSiteTransportImpact),
        color: this.colorsService.ONSITE_TRANSPORT_COLOR
      });
    }

    // Add other categories
    const accommodationImpact = config.viewMode === 'group'
      ? config.tripImpact.accommodationImpact
      : config.tripImpact.accommodationPerTraveller;

    if (accommodationImpact > 0) {
      data.push({
        label: this.transloco.translate('impact.categories.accommodation'),
        value: Math.round(accommodationImpact),
        color: this.colorsService.ACCOMMODATION_COLOR
      });
    }

    const activityImpact = config.viewMode === 'group'
      ? config.tripImpact.activityImpact
      : config.tripImpact.activityPerTraveller;

    if (activityImpact > 0) {
      data.push({
        label: this.transloco.translate('impact.categories.activities'),
        value: Math.round(activityImpact),
        color: this.colorsService.ACTIVITY_COLOR
      });
    }

    const foodImpact = config.viewMode === 'group'
      ? config.tripImpact.foodImpact
      : config.tripImpact.foodPerTraveller;

    if (foodImpact > 0) {
      data.push({
        label: this.transloco.translate('impact.categories.food'),
        value: Math.round(foodImpact),
        color: this.colorsService.FOOD_COLOR
      });
    }

    const wasteImpact = config.viewMode === 'group'
      ? config.tripImpact.wasteImpact
      : config.tripImpact.wastePerTraveller;

    if (wasteImpact > 0) {
      data.push({
        label: this.transloco.translate('impact.categories.waste'),
        value: Math.round(wasteImpact),
        color: this.colorsService.WASTE_COLOR
      });
    }

    // Sort by impact descending
    return data.sort((a, b) => b.value - a.value);
  }

  /**
   * Generate legend data organized by category
   */
  private generateLegendDataByCategory(config: ImpactDataConfig): LegendCategory[] {
    const categories: LegendCategory[] = [];
    const totalImpact = this.calculateTotalImpact(config);

    // Helper to create a category
    const createCategory = (
      category: 'transport' | 'accommodation' | 'activity' | 'food' | 'waste',
      translationKey: string,
      elements: Array<{ type: string; impact: number; title?: string }>
    ): LegendCategory => {
      if (elements.length === 0) {
        return {
          name: this.transloco.translate(translationKey) || translationKey,
          translationKey,
          baseColor: this.colorsService.getCategoryColor(category),
          totalImpact: 0,
          percentage: 0,
          isExpanded: config.categoryStateManager.isExpanded(translationKey),
          items: []
        };
      }

      // Group by type + title (same logic as chart generation for hover sync)
      const groupMap = new Map<string, { type: string; impact: number; title?: string; isEcoFriendly?: boolean }>();
      elements.forEach(el => {
        const isEco = (el as { isEcoFriendly?: boolean }).isEcoFriendly;
        const ecoKey = category === 'accommodation' ? (isEco ? '-eco' : '-std') : '';
        const key = el.title ? `${el.type}-${el.title}${ecoKey}` : `${el.type}${ecoKey}`;
        const existing = groupMap.get(key);
        if (existing) {
          existing.impact += el.impact;
        } else {
          groupMap.set(key, { type: el.type, impact: el.impact, title: el.title, isEcoFriendly: isEco });
        }
      });

      const groupedElements = Array.from(groupMap.values()).sort((a, b) => b.impact - a.impact);

      const adjustedElements = groupedElements.map(el => ({
        ...el,
        adjustedImpact: config.viewMode === 'individual'
          ? el.impact / (config.travelers || 1)
          : el.impact
      }));

      const categoryTotalImpact = adjustedElements.reduce((sum, el) => sum + el.adjustedImpact, 0);
      const categoryColors = this.colorsService.generateCategoryGradient(category, adjustedElements.length);

      const items = adjustedElements.map((el, index) => ({
        label: translateElementType(this.transloco, el.type),
        title: el.title,
        impact: Math.round(el.adjustedImpact),
        percentage: totalImpact > 0 ? Math.round((el.adjustedImpact / totalImpact) * 100) : 0,
        color: categoryColors[index] || this.colorsService.getCategoryColor(category),
        type: el.type,
        isEcoFriendly: el.isEcoFriendly
      }));

      return {
        name: this.transloco.translate(translationKey) || translationKey,
        translationKey,
        baseColor: this.colorsService.getCategoryColor(category),
        totalImpact: Math.round(categoryTotalImpact),
        percentage: totalImpact > 0 ? Math.round((categoryTotalImpact / totalImpact) * 100) : 0,
        isExpanded: config.categoryStateManager.isExpanded(translationKey),
        items
      };
    };

    // Transport (with possible main transport exclusion)
    const transportElements = config.excludeMainTransport
      ? config.fullImpactDetails.transport.filter(t => !t.isMainTransport)
      : config.fullImpactDetails.transport;

    const transportCategory = createCategory('transport', 'impact.categories.transport', transportElements);
    if (transportCategory.items.length > 0) {
      categories.push(transportCategory);
    }

    // Accommodation
    const accommodationCategory = createCategory('accommodation', 'impact.categories.accommodation', config.fullImpactDetails.accommodation);
    if (accommodationCategory.items.length > 0) {
      categories.push(accommodationCategory);
    }

    // Activities
    const activityCategory = createCategory('activity', 'impact.categories.activities', config.fullImpactDetails.activities);
    if (activityCategory.items.length > 0) {
      categories.push(activityCategory);
    }

    // Food
    const foodCategory = createCategory('food', 'impact.categories.food', config.fullImpactDetails.food);
    if (foodCategory.items.length > 0) {
      categories.push(foodCategory);
    }

    // Waste (special handling as it's a global value)
    const wasteImpact = config.viewMode === 'group'
      ? config.tripImpact.wasteImpact
      : config.tripImpact.wastePerTraveller;

    if (wasteImpact > 0) {
      categories.push({
        name: this.transloco.translate('impact.categories.waste') || 'Déchets',
        translationKey: 'impact.categories.waste',
        baseColor: this.colorsService.getCategoryColor('waste'),
        totalImpact: Math.round(wasteImpact),
        percentage: totalImpact > 0 ? Math.round((wasteImpact / totalImpact) * 100) : 0,
        isExpanded: config.categoryStateManager.isExpanded('impact.categories.waste'),
        items: [{
          label: this.transloco.translate('impact.categories.waste') || 'Déchets',
          impact: Math.round(wasteImpact),
          percentage: totalImpact > 0 ? Math.round((wasteImpact / totalImpact) * 100) : 0,
          color: this.colorsService.getCategoryColor('waste'),
          type: 'waste'
        }]
      });
    }

    // Sort categories by impact descending
    return categories.sort((a, b) => b.totalImpact - a.totalImpact);
  }

  /**
   * Generate legend data with transport grouping (main vs on-site)
   */
  private generateLegendDataWithTransportGrouping(config: ImpactDataConfig): LegendCategory[] {
    const categories: LegendCategory[] = [];
    const totalImpact = this.calculateTotalImpact(config);

    // Calculate main and on-site transport impacts
    let mainTransportImpact = 0;
    let onSiteTransportImpact = 0;

    const mainTransports: Array<{ type: string; impact: number; title?: string }> = [];
    const onSiteTransports: Array<{ type: string; impact: number; title?: string }> = [];

    config.fullImpactDetails.transport.forEach(transport => {
      if (transport.isMainTransport) {
        mainTransportImpact += transport.impact;
        mainTransports.push(transport);
      } else {
        onSiteTransportImpact += transport.impact;
        onSiteTransports.push(transport);
      }
    });

    // Apply view mode
    if (config.viewMode === 'individual') {
      mainTransportImpact = mainTransportImpact / (config.travelers || 1);
      onSiteTransportImpact = onSiteTransportImpact / (config.travelers || 1);
    }

    // Main transport category
    if (mainTransportImpact > 0 && !config.excludeMainTransport) {
      const adjustedMainTransports = mainTransports
        .map(t => ({
          ...t,
          adjustedImpact: config.viewMode === 'individual' ? t.impact / (config.travelers || 1) : t.impact
        }))
        .sort((a, b) => b.adjustedImpact - a.adjustedImpact);

      const mainTransportColors = this.colorsService.generateCategoryGradient('transport', adjustedMainTransports.length);

      categories.push({
        name: this.transloco.translate('impact.transportTypes.mainTransport'),
        translationKey: 'impact.transportTypes.mainTransport',
        baseColor: this.colorsService.MAIN_TRANSPORT_COLOR,
        totalImpact: Math.round(mainTransportImpact),
        percentage: totalImpact > 0 ? Math.round((mainTransportImpact / totalImpact) * 100) : 0,
        isExpanded: config.categoryStateManager.isExpanded('impact.transportTypes.mainTransport'),
        itemsAreDetailsOnly: true,
        items: adjustedMainTransports.map((t, index) => ({
          label: translateElementType(this.transloco, t.type),
          title: t.title,
          impact: Math.round(t.adjustedImpact),
          percentage: totalImpact > 0 ? Math.round((t.adjustedImpact / totalImpact) * 100) : 0,
          color: mainTransportColors[index] || this.colorsService.MAIN_TRANSPORT_COLOR,
          type: t.type
        }))
      });
    }

    // On-site transport category
    if (onSiteTransportImpact > 0) {
      const adjustedOnSiteTransports = onSiteTransports
        .map(t => ({
          ...t,
          adjustedImpact: config.viewMode === 'individual' ? t.impact / (config.travelers || 1) : t.impact
        }))
        .sort((a, b) => b.adjustedImpact - a.adjustedImpact);

      const onSiteTransportColors = this.colorsService.generateCategoryGradient('transport', adjustedOnSiteTransports.length);

      categories.push({
        name: this.transloco.translate('impact.transportTypes.onSiteTransport'),
        translationKey: 'impact.transportTypes.onSiteTransport',
        baseColor: this.colorsService.ONSITE_TRANSPORT_COLOR,
        totalImpact: Math.round(onSiteTransportImpact),
        percentage: totalImpact > 0 ? Math.round((onSiteTransportImpact / totalImpact) * 100) : 0,
        isExpanded: config.categoryStateManager.isExpanded('impact.transportTypes.onSiteTransport'),
        itemsAreDetailsOnly: true,
        items: adjustedOnSiteTransports.map((t, index) => ({
          label: translateElementType(this.transloco, t.type),
          title: t.title,
          impact: Math.round(t.adjustedImpact),
          percentage: totalImpact > 0 ? Math.round((t.adjustedImpact / totalImpact) * 100) : 0,
          color: onSiteTransportColors[index] || this.colorsService.ONSITE_TRANSPORT_COLOR,
          type: t.type
        }))
      });
    }

    // Accommodation category
    const accommodationImpact = config.viewMode === 'group'
      ? config.tripImpact.accommodationImpact
      : config.tripImpact.accommodationPerTraveller;

    if (accommodationImpact > 0) {
      const adjustedAccommodations = config.fullImpactDetails.accommodation
        .map(a => ({
          ...a,
          adjustedImpact: config.viewMode === 'individual' ? a.impact / (config.travelers || 1) : a.impact
        }))
        .sort((a, b) => b.adjustedImpact - a.adjustedImpact);

      const accommodationColors = this.colorsService.generateCategoryGradient('accommodation', adjustedAccommodations.length);

      categories.push({
        name: this.transloco.translate('impact.categories.accommodation'),
        translationKey: 'impact.categories.accommodation',
        baseColor: this.colorsService.ACCOMMODATION_COLOR,
        totalImpact: Math.round(accommodationImpact),
        percentage: totalImpact > 0 ? Math.round((accommodationImpact / totalImpact) * 100) : 0,
        isExpanded: config.categoryStateManager.isExpanded('impact.categories.accommodation'),
        itemsAreDetailsOnly: true,
        items: adjustedAccommodations.map((a, index) => ({
          label: translateElementType(this.transloco, a.type),
          title: a.title,
          impact: Math.round(a.adjustedImpact),
          percentage: totalImpact > 0 ? Math.round((a.adjustedImpact / totalImpact) * 100) : 0,
          color: accommodationColors[index] || this.colorsService.ACCOMMODATION_COLOR,
          type: a.type,
          isEcoFriendly: a.isEcoFriendly
        }))
      });
    }

    // Activities category
    const activityImpact = config.viewMode === 'group'
      ? config.tripImpact.activityImpact
      : config.tripImpact.activityPerTraveller;

    if (activityImpact > 0) {
      const adjustedActivities = config.fullImpactDetails.activities
        .map(a => ({
          ...a,
          adjustedImpact: config.viewMode === 'individual' ? a.impact / (config.travelers || 1) : a.impact
        }))
        .sort((a, b) => b.adjustedImpact - a.adjustedImpact);

      const activityColors = this.colorsService.generateCategoryGradient('activity', adjustedActivities.length);

      categories.push({
        name: this.transloco.translate('impact.categories.activities'),
        translationKey: 'impact.categories.activities',
        baseColor: this.colorsService.ACTIVITY_COLOR,
        totalImpact: Math.round(activityImpact),
        percentage: totalImpact > 0 ? Math.round((activityImpact / totalImpact) * 100) : 0,
        isExpanded: config.categoryStateManager.isExpanded('impact.categories.activities'),
        itemsAreDetailsOnly: true,
        items: adjustedActivities.map((a, index) => ({
          label: translateElementType(this.transloco, a.type),
          title: a.title,
          impact: Math.round(a.adjustedImpact),
          percentage: totalImpact > 0 ? Math.round((a.adjustedImpact / totalImpact) * 100) : 0,
          color: activityColors[index] || this.colorsService.ACTIVITY_COLOR,
          type: a.type
        }))
      });
    }

    // Food category
    const foodImpact = config.viewMode === 'group'
      ? config.tripImpact.foodImpact
      : config.tripImpact.foodPerTraveller;

    if (foodImpact > 0) {
      const adjustedFood = config.fullImpactDetails.food
        .map(f => ({
          ...f,
          adjustedImpact: config.viewMode === 'individual' ? f.impact / (config.travelers || 1) : f.impact
        }))
        .sort((a, b) => b.adjustedImpact - a.adjustedImpact);

      const foodColors = this.colorsService.generateCategoryGradient('food', adjustedFood.length);

      categories.push({
        name: this.transloco.translate('impact.categories.food'),
        translationKey: 'impact.categories.food',
        baseColor: this.colorsService.FOOD_COLOR,
        totalImpact: Math.round(foodImpact),
        percentage: totalImpact > 0 ? Math.round((foodImpact / totalImpact) * 100) : 0,
        isExpanded: config.categoryStateManager.isExpanded('impact.categories.food'),
        itemsAreDetailsOnly: true,
        items: adjustedFood.map((f, index) => ({
          label: translateElementType(this.transloco, f.type),
          impact: Math.round(f.adjustedImpact),
          percentage: totalImpact > 0 ? Math.round((f.adjustedImpact / totalImpact) * 100) : 0,
          color: foodColors[index] || this.colorsService.FOOD_COLOR,
          type: f.type
        }))
      });
    }

    // Waste category
    const wasteImpact = config.viewMode === 'group'
      ? config.tripImpact.wasteImpact
      : config.tripImpact.wastePerTraveller;

    if (wasteImpact > 0) {
      categories.push({
        name: this.transloco.translate('impact.categories.waste'),
        translationKey: 'impact.categories.waste',
        baseColor: this.colorsService.WASTE_COLOR,
        totalImpact: Math.round(wasteImpact),
        percentage: totalImpact > 0 ? Math.round((wasteImpact / totalImpact) * 100) : 0,
        isExpanded: config.categoryStateManager.isExpanded('impact.categories.waste'),
        itemsAreDetailsOnly: true,
        items: [{
          label: this.transloco.translate('impact.categories.waste'),
          impact: Math.round(wasteImpact),
          percentage: totalImpact > 0 ? Math.round((wasteImpact / totalImpact) * 100) : 0,
          color: this.colorsService.WASTE_COLOR,
          type: 'waste'
        }]
      });
    }

    // Sort categories by impact descending
    return categories.sort((a, b) => b.totalImpact - a.totalImpact);
  }
}
